import './alteration-line.scss'

import _ from 'lodash'
import { first } from 'rxjs/operators'
import { defineComponent, PropType } from 'vue'
import { BModal } from 'bootstrap-vue'
import { faTimesCircle, faComment, faImage } from '@fortawesome/free-regular-svg-icons'

import { Alteration, createTranslatedString, editTranslatedString, getTranslatedString } from '@skinsoft/types'
import { preferredTerm } from '@skinsoft/selectors/thesaurus-term-selectors'

import { reactiveVueMixin } from "@skinsoft/reactive-vue"
import { EntitySelect } from '@skinsoft/ui'
import ThesaurusTermTree from '@skinsoft/ui/src/thesaurus-term-tree'
import { THESAURUS_TERM_SELECTED } from '@skinsoft/ui/src/thesaurus-term-tree/thesaurus-term-tree-modal'
import { AutoCompleteTextField2, FIELD_VALUE_UPDATED } from '@skinsoft/ui2'
import { AlterationDetailImagePopup } from '../alteration-detail-image-popup'
import { DETAILED_ALTERATION_IMAGE_ADDED } from '../../../../events'
import { DELETE_IMAGE_CLICK_TOPIC } from '@skinsoft/bus'
import { DeleteImageClickEvent } from '@skinsoft/entity-ui'

export const DELETE_ALTERATION = 'delete-alteration'
export const ALTERATION_EDITED = 'alteration-edited'

export interface AlterationChanged {
    alterationNumber: number,
    updates: Partial<Alteration>
}

export default defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sEntitySelect: EntitySelect,
        sThesaurusTermTree: ThesaurusTermTree,
        sAutoCompleteTextField2: AutoCompleteTextField2,
        sAlterationDetailImagePopup: AlterationDetailImagePopup,
    },
    props: {
        alteration: {
            type: Object as PropType<Alteration>,
        },
        alterationNumber: { type: Number },
        readOnly: { type: Boolean },
        existingTitles: {
            type: Array as PropType<String[]>,
            default: () => []
        },
    },
    template: require('./alteration-line.html'),
    data() {
        return {
            selectedAlterationType: '', // id of ThesaurusTerm
            alterationCopy: null as Alteration | null,
            isOnline: window.navigator.onLine, // TEMPORARY to disable detailed view offline

            deleteIcon: faTimesCircle,
            commentIcon: faComment,
            imageIcon: faImage,

            DELETE_ALTERATION: DELETE_ALTERATION,
            THESAURUS_TERM_SELECTED: THESAURUS_TERM_SELECTED,
            FIELD_VALUE_UPDATED: FIELD_VALUE_UPDATED,
            DETAILED_ALTERATION_IMAGE_ADDED: DETAILED_ALTERATION_IMAGE_ADDED,
            DELETE_IMAGE_CLICK_TOPIC: DELETE_IMAGE_CLICK_TOPIC,
        }
    },
    mounted() {
        if(this.alteration) {
            this.alterationCopy = _.cloneDeep(this.alteration)
            if(this.alteration.typeOfAlteration) {
                this.selectedAlterationType = this.alteration.typeOfAlteration
            }
        }

        // TEMPORARY to disable detailed view offline
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)
    },
    computed: {
        precision: {
            get(): string {
                if(this.alterationCopy) {
                    return getTranslatedString(this.alterationCopy.precision, this.getAppLocale())
                }

                return ''
            },
            set(value: string) {
                if(this.alterationCopy) {
                    if(this.alterationCopy.precision) {
                        editTranslatedString(this.alterationCopy.precision, value, this.getAppLocale())
                    } else {
                        _.set(this.alterationCopy, 'precision', createTranslatedString(this.getAppLocale(), value))
                    }
                }
            },
        },
        title: {
            get(): string {
                if(this.alterationCopy) {
                    return getTranslatedString(this.alterationCopy.title, this.getAppLocale())
                }

                return ''
            },
            set(value: string) {
                if(this.alterationCopy) {
                    if(this.alterationCopy.title) {
                        editTranslatedString(this.alterationCopy.title, value, this.getAppLocale())
                    } else {
                        _.set(this.alterationCopy, 'title', createTranslatedString(this.getAppLocale(), value))
                    }

                    const updates: Partial<Alteration> = {
                        title: this.alterationCopy.title,
                    }
                    this.$emit(
                        ALTERATION_EDITED,
                        {
                            alterationNumber: this.alterationNumber,
                            updates
                        } as AlterationChanged
                    )
                }
            },
        },
        detailedImages(): string[] {
            return this.alterationCopy?.detailedViews ?? []
        },
    },
    methods: {
        // TEMPORARY to disable detailed view offline
        updateConnexionStatus() {
            this.isOnline = window.navigator.onLine
        },

        async getThesaurusTermNameIfExists(id: string) {
            if (id) {
                // $thesaurusTermDataStore is injected globally on Vue.prototype by ThesaurusTermDataStore's plugin install
                const thesaurusTerm = await (this as any).$thesaurusTermDataStore.fetchOne(id).pipe(first()).toPromise();
                if (thesaurusTerm) {
                    return getTranslatedString(
                        preferredTerm(thesaurusTerm),
                        this.getAppLocale()
                    )
                }
            }
            return ''
        },

        onThesaurusTermSelected(thesaurusTermId: string) {
            if(thesaurusTermId) {
                const updates: Partial<Alteration> = {
                    typeOfAlteration: thesaurusTermId
                }

                this.selectedAlterationType = thesaurusTermId
                this.$emit(
                    ALTERATION_EDITED,
                    {
                        alterationNumber: this.alterationNumber,
                        updates
                    } as AlterationChanged
                )
            }
        },

        openPrecisionModal() {
            // @ts-ignore
            this.$refs.precisionModal.show()
        },

        onAlterationPrecisionEdited() {
            if(this.alterationCopy) {
                const updates: Partial<Alteration> = {
                    precision: this.alterationCopy.precision,
                }

                this.$emit(
                    ALTERATION_EDITED,
                    {
                        alterationNumber: this.alterationNumber,
                        updates
                    } as AlterationChanged
                )
            }
            // @ts-ignore
            this.$refs.precisionModal.hide()
        },

        deleteAlteration() {
            if(!this.readOnly) {
                this.$emit(DELETE_ALTERATION, this.alterationNumber)
            }
        },

        openDetailedImageModal() {
            (this.$refs.detailedImagesModal as InstanceType<typeof AlterationDetailImagePopup>).openModal()
        },

        onDetailedImageAdded(event) {
            if(event.cacheKey) {
                if(this.alterationCopy) {
                    if(this.alterationCopy.detailedViews) {
                        this.alterationCopy.detailedViews.push(event.cacheKey)
                    } else {
                        this.alterationCopy.detailedViews = [event.cacheKey]
                    }

                    const updates: Partial<Alteration> = {
                        detailedViews: this.alterationCopy.detailedViews,
                    }
                    this.$emit(
                        ALTERATION_EDITED,
                        {
                            alterationNumber: this.alterationNumber,
                            updates
                        } as AlterationChanged
                    )
                }
            }
        },

        onDetailedImageDeleted(event: DeleteImageClickEvent) {
            if(this.alterationCopy?.detailedViews?.length) {
                if(event.imageId) {
                    this.alterationCopy.detailedViews = this.alterationCopy.detailedViews.filter(mediaId => {
                        return mediaId !== event.imageId
                    })

                    const updates: Partial<Alteration> = {
                        detailedViews: this.alterationCopy.detailedViews,
                    }
                    this.$emit(
                        ALTERATION_EDITED,
                        {
                            alterationNumber: this.alterationNumber,
                            updates
                        } as AlterationChanged
                    )
                }
            }
        },

        onTitleUpdated(value: String) {
            this.title = value as string
        },
    },
})
